/**
 * Review Planner helpers — pure functions for the Reviews surface.
 *
 * Month rules, the on-load rollover reconciliation, and the atwork
 * export (.xlsx) row-parsing rules all live here so they can be
 * reasoned about (and unit-tested) without touching React or Supabase.
 *
 * HARD CONTRACT (CLAUDE.md §34.7): nothing in this module reads from or
 * feeds into file statistics, prep-request statistics, the bonus
 * counter, or SLA calculations. The review planner is self-contained.
 */

// Shared business-day helper — reuses the SLA logic's SA public-holiday
// data (src/config/zaPublicHolidays.js) via businessDays.js. Only the
// holiday/working-day utility is imported; nothing here couples to
// slaMonitor, slaCalculator, or any stats module (§34.7).
import { isBusinessDay } from './businessDays';
import { getMonthKeySAST, getTodaySASTDateString } from './dateHelpers';

/** Month numbers that may hold reviews. Jan (1) and Dec (12) are kept
 *  clear by design — no annual reviews are ever scheduled in them. */
export const OPEN_MONTHS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

export const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function isOpenMonth(month) {
  return OPEN_MONTHS.includes(month);
}

/** Uppercase 3-letter English month abbreviation ("NOV") — used by the
 *  calendar card's header band. */
export function monthAbbrev(month) {
  return (MONTH_NAMES[month] || '').slice(0, 3).toUpperCase();
}

/** Phase 2 status flow (CLAUDE.md §34.9):
 *  planned → submitted → appointment_made → completed.
 *  'submitted' and 'appointment_made' are the two statuses that render a
 *  calendar card on the shared pipeline. */
export const PIPELINE_REVIEW_STATUSES = ['submitted', 'appointment_made'];

// Keys are the INTERNAL status values stored in `review_clients.status`
// (never renamed); the values are DISPLAY text only — 'submitted' renders as
// "Inbox", matching the first pipeline column's label.
export const REVIEW_STATUS_LABELS = {
  planned: 'Planned',
  submitted: 'Inbox',
  appointment_made: 'Appointment made',
  completed: 'Completed',
};

/** Which pipeline column a calendar card renders in, by status. */
export const REVIEW_PIPELINE_COLUMNS = {
  submitted: 'submitted',
  appointment_made: 'in_progress',
};

/**
 * Calendar CARDS for one pipeline column (CLAUDE.md §34.10): one card
 * per UNIT — a whole group renders a single card (its members share
 * status and timestamps by invariant), an ungrouped client renders its
 * own. Oldest submission first. Completed reviews never render — a
 * completed unit simply stops matching this filter.
 *
 * Card descriptor: { key, isGroup, groupId, label, members, status,
 * submittedAt } — for a single card `members` has one element and
 * `label` is the client's natural display name.
 */
export function getReviewCardsForColumn(reviewClients, stageKey, groupLabels = {}) {
  const inColumn = (reviewClients || []).filter(
    (c) => REVIEW_PIPELINE_COLUMNS[c.status] === stageKey
  );
  const cards = [];
  const seenGroups = new Set();
  for (const c of inColumn) {
    if (!c.groupId) {
      cards.push({
        key: c.id,
        isGroup: false,
        groupId: null,
        label: c.displayName,
        members: [c],
        status: c.status,
        submittedAt: c.submittedAt,
      });
    } else if (!seenGroups.has(c.groupId)) {
      seenGroups.add(c.groupId);
      const rawMembers = inColumn.filter((m) => m.groupId === c.groupId);
      const { individuals, entities } = sortMonthClients(rawMembers);
      const members = [...individuals, ...entities];
      cards.push({
        key: `group:${c.groupId}`,
        isGroup: true,
        groupId: c.groupId,
        label: groupLabels[c.groupId] || groupFallbackLabel(members),
        members,
        status: members[0].status,
        submittedAt: members[0].submittedAt,
      });
    }
  }
  return cards.sort((a, b) => new Date(a.submittedAt || 0) - new Date(b.submittedAt || 0));
}

/** Default / fallback label for a group: the lead's surname (lead =
 *  first non-entity member), else the first member's display name.
 *  Used when creating a group and when a stored label is blank. */
export function groupFallbackLabel(sortedMembers) {
  const lead = sortedMembers.find((m) => !m.isEntity);
  if (lead) return individualSurnameKey(lead);
  return sortedMembers[0]?.displayName || 'Group';
}

/**
 * Flat month UNITS (CLAUDE.md §34.10): groups and ungrouped clients
 * interleaved in one alphabetical list — the Individuals / Companies
 * sections are gone. Sort key: group → lead surname (all-entity groups
 * → the group label); ungrouped individual → surname key; ungrouped
 * entity → display name. Case/diacritic-insensitive throughout.
 *
 * Group members are ordered individuals-then-entities inside the block,
 * and EVERY member is always present — display code must never collapse
 * or truncate a group (hard rule, §34.10).
 */
export function buildMonthUnits(clients, groupLabels = {}) {
  const byGroup = new Map();
  const singles = [];
  for (const c of clients || []) {
    if (c.groupId) {
      if (!byGroup.has(c.groupId)) byGroup.set(c.groupId, []);
      byGroup.get(c.groupId).push(c);
    } else {
      singles.push(c);
    }
  }

  const units = [];
  for (const [groupId, rawMembers] of byGroup) {
    const { individuals, entities } = sortMonthClients(rawMembers);
    const members = [...individuals, ...entities];
    const label = groupLabels[groupId] || groupFallbackLabel(members);
    const sortKey = individuals.length > 0 ? individualSurnameKey(individuals[0]) : label;
    units.push({ type: 'group', groupId, label, members, sortKey });
  }
  for (const c of singles) {
    units.push({
      type: 'client',
      client: c,
      sortKey: c.isEntity ? (c.displayName || '') : individualSurnameKey(c),
    });
  }
  return units.sort((a, b) => compareNames(a.sortKey, b.sortKey));
}

/** Fold case + diacritics for exact key matching (the sort comparisons
 *  use localeCompare; hint matching needs a canonical map key). */
function foldName(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * "Possibly related" hint (CLAUDE.md §34.10) — render-time only, never
 * persisted, computed per month among UNGROUPED clients only:
 *   - an individual is tagged when another ungrouped unit shares its
 *     surname key (another individual with the same surname, or an
 *     entity whose name contains that surname as a whole word);
 *   - an entity is tagged when any ungrouped individual's surname in
 *     the month appears as a whole word in its display name
 *     ("Botes Familie Trust" matches an ungrouped "Botes").
 * Tags disappear the moment the clients are grouped. Pure hint.
 *
 * @returns {Set<string>} ids of clients to tag RELATED?
 */
export function computeRelatedHints(monthClients) {
  const ungrouped = (monthClients || []).filter((c) => !c.groupId);
  const individuals = ungrouped.filter((c) => !c.isEntity);
  const entities = ungrouped.filter((c) => c.isEntity);

  const surnameOf = (c) => foldName(individualSurnameKey(c));
  const surnameCounts = new Map();
  for (const ind of individuals) {
    const key = surnameOf(ind);
    if (key) surnameCounts.set(key, (surnameCounts.get(key) || 0) + 1);
  }

  const entityWords = entities.map((e) => ({
    id: e.id,
    words: new Set(foldName(e.displayName).split(/[^a-z0-9]+/).filter(Boolean)),
  }));

  const related = new Set();
  for (const ind of individuals) {
    const key = surnameOf(ind);
    if (!key) continue;
    const sharesWithIndividual = surnameCounts.get(key) > 1;
    const matchingEntity = entityWords.find((e) => e.words.has(key));
    if (sharesWithIndividual || matchingEntity) related.add(ind.id);
    if (matchingEntity) related.add(matchingEntity.id);
  }
  // An entity can match several individuals; make sure every matching
  // entity is tagged (the loop above only tags the first find).
  for (const e of entityWords) {
    if (related.has(e.id)) continue;
    if (individuals.some((ind) => e.words.has(surnameOf(ind)))) related.add(e.id);
  }
  return related;
}

/**
 * Today's year and month in Africa/Johannesburg (SAST), as numbers.
 * Derived from the shared SAST month key ("YYYY-MM") in dateHelpers so the
 * timezone logic lives in one place.
 */
export function getCurrentSASTYearMonth() {
  const [year, month] = getMonthKeySAST(new Date()).split('-');
  return { year: parseInt(year, 10), month: parseInt(month, 10) };
}

/**
 * PERMANENT-ANCHOR PRINCIPLE (CLAUDE.md §34.4). A client's review_month
 * is a permanent anchor in the Adviser's annual cycle. It is changed by
 * the Adviser manually (drag, or the detail modal's month select) and by
 * NOTHING else — no rollover, no month-end sweep, no reconciliation,
 * ever. A review finished late (or early) does not move the client; they
 * remain in their month and recur there next cycle. Cross-month
 * persistence of unfinished work is the job of the Phase 2 pipeline
 * calendar card — NOT the planner.
 *
 * Whether a review is overdue is therefore DERIVED at render time, never
 * persisted: a client is overdue when their review month has passed
 * (relative to the current SAST date), the review is not completed, AND
 * the applicable checklist items are not all ticked — a fully-ticked
 * review stops reading as overdue even before the status flow reaches
 * 'completed' (Adviser decision). Months in the future — or the current
 * month itself — are never overdue. (The legacy rolled_from_month /
 * is_overdue columns remain in the table but are unused and ignored by
 * the app.)
 *
 * @param {object} client — camelCase review client (reviewYear, reviewMonth,
 *   status + checklist fields)
 * @param {{year: number, month: number}} now — getCurrentSASTYearMonth()
 */
export function isReviewOverdue(client, now) {
  if (!client || client.status === 'completed') return false;
  if (allApplicableItemsTicked(client)) return false;
  return (
    client.reviewYear < now.year ||
    (client.reviewYear === now.year && client.reviewMonth < now.month)
  );
}

/**
 * True when a planner month/year target lies in the past relative to the
 * current SAST date. Used by the import and add-client modals to show an
 * informational "already passed" note — purely informative: nothing
 * blocks, nothing auto-moves.
 */
export function isPastPlannerMonth(year, month, now) {
  return year < now.year || (year === now.year && month < now.month);
}

/**
 * Normalise one cell of the atwork export. Runs BEFORE any other rule:
 * trim, collapse internal repeated whitespace (the February export has
 * trailing spaces like "Erna "), and treat empty strings and the
 * literal "NaN" (which atwork emits for blank fields) as null — the
 * string "NaN" must never be rendered or stored. A lone "-" also
 * normalises to null EXCEPT in the initials field, where it is the
 * entity marker and must survive for entity detection.
 */
function cleanCell(value, { keepDash = false } = {}) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(/\s+/g, ' ');
  if (text === '' || text.toLowerCase() === 'nan') return null;
  if (text === '-' && !keepDash) return null;
  return text;
}

/**
 * Normalise a header cell for synonym matching: trim, lowercase, strip
 * surrounding square brackets, strip a leading "client_", and remove
 * underscores. This makes both known atwork export formats (and
 * hopefully future ones) resolve to the same field names:
 *   "Client_Surname" → "surname"     "[Surname]"  → "surname"
 *   "Client_NickName" → "nickname"   "[Nickname]" → "nickname"
 *   "Client_Initials" → "initials"   "[FullName]" → "fullname"
 */
function normaliseHeader(cell) {
  let h = String(cell ?? '').trim().toLowerCase();
  h = h.replace(/^\[/, '').replace(/\]$/, '').trim();
  if (h.startsWith('client_')) h = h.slice('client_'.length);
  return h.replace(/_/g, '');
}

/** Normalised header names the parser recognises, mapped to fields.
 *  Only "surname" is hard-required; the rest are optional and any
 *  unrecognised extra columns are simply ignored. */
const ATWORK_HEADER_FIELDS = ['surname', 'initials', 'nickname', 'title', 'fullname'];

/**
 * Parse rows from the atwork export's first sheet (as produced by
 * XLSX.utils.sheet_to_json with header:1 — an array of arrays where
 * row 0 is the header row).
 *
 * Rules (CLAUDE.md §34.3). Two known export formats are accepted:
 *   Format A: Client_Surname | Client_Initials | Client_NickName | Client_Title
 *   Format B: [Surname] | [Initials] | [FullName] | [Title] | [Nickname]
 * Columns are matched by NORMALISED header name (normaliseHeader above),
 * never by position, so column order changes and future header variants
 * that normalise to the same names are tolerated.
 *   - Only a surname column is required; a missing one throws with the
 *     actual headers found, so an unknown format self-diagnoses.
 *   - Entity detection: initials === "-" OR (nickname blank AND
 *     fullname blank). Entities use the surname verbatim (post-trim).
 *   - Individuals: display name = `${first} ${surname}` where first is
 *     nickname → first word of fullname → initials (first non-blank).
 *     fullname is used only for this derivation and is never persisted.
 *   - Empty / "NaN" / stray "-" cells become null; fully empty rows are
 *     skipped.
 *
 * @param {Array<Array>} rows — sheet_to_json(sheet, { header: 1 }) output
 * @returns {Array<object>} parsed clients:
 *   { displayName, clientSurname, clientInitials, clientNickname,
 *     clientTitle, isEntity, sourceString }
 */
export function parseAtworkRows(rows) {
  if (!rows || rows.length === 0) {
    throw new Error('The spreadsheet appears to be empty.');
  }

  // Resolve each recognised field to a column index via the normalised
  // header names. Duplicated headers keep the first occurrence.
  const headerRow = rows[0].map((h) => String(h ?? '').trim());
  const columnIndex = {};
  headerRow.forEach((header, idx) => {
    const field = normaliseHeader(header);
    if (ATWORK_HEADER_FIELDS.includes(field) && !(field in columnIndex)) {
      columnIndex[field] = idx;
    }
  });

  if (!('surname' in columnIndex)) {
    const found = headerRow.filter(Boolean).join(', ') || '(no headers)';
    throw new Error(
      'Could not find a surname column. Recognised headers include ' +
      '"Client_Surname" or "[Surname]". Found: ' + found + '.'
    );
  }

  const cellAt = (row, field, opts) =>
    field in columnIndex ? cleanCell(row[columnIndex[field]], opts) : null;

  const parsed = [];
  for (const row of rows.slice(1)) {
    if (!row || row.every((cell) => cleanCell(cell, { keepDash: true }) === null)) continue; // fully empty row

    const surname = cellAt(row, 'surname');
    const initials = cellAt(row, 'initials', { keepDash: true }); // "-" is the entity marker
    const nickname = cellAt(row, 'nickname');
    const title = cellAt(row, 'title');
    const fullname = cellAt(row, 'fullname');

    // Entity when explicitly marked with "-" initials, or when the row
    // has no personal name fields at all (catches entity rows exported
    // with real-looking initials, e.g. "ii", but no name).
    const isEntity = initials === '-' || (!nickname && !fullname);

    let displayName;
    if (isEntity) {
      displayName = surname || '';
    } else {
      // First-name fallback chain: nickname → first word of fullname →
      // initials.
      const first = nickname || (fullname ? fullname.split(' ')[0] : null) || initials;
      displayName = [first, surname].filter(Boolean).join(' ').trim();
    }

    // A row that can't produce a display name would import as a blank
    // planner entry — skip it.
    if (!displayName) continue;

    // What the Adviser sees in the preview as "where this name came
    // from" — built only from the fields present, so no format renders
    // blanks or "undefined".
    const sourceString = isEntity
      ? (surname || '')
      : [title, initials, surname].filter(Boolean).join(' ').trim();

    parsed.push({
      displayName,
      clientSurname: surname,
      clientInitials: initials,
      clientNickname: nickname,
      clientTitle: title,
      isEntity,
      sourceString,
    });
  }

  return parsed;
}

/**
 * The alphabetical sort key for an individual: the surname column when
 * present, otherwise the LAST word of the display name (manually added
 * clients store only display_name). Single-word display names use the
 * whole name.
 */
function individualSurnameKey(client) {
  if (client.clientSurname) return client.clientSurname;
  const words = (client.displayName || '').trim().split(' ');
  return words[words.length - 1] || '';
}

// Case- and diacritic-insensitive compare, so "van Heerden" /
// "Van Der Merwe" ordering is stable regardless of capitalisation.
const compareNames = (a, b) =>
  a.localeCompare(b, undefined, { sensitivity: 'base' });

/**
 * Section-and-sort a month's clients for display (CLAUDE.md §34.2):
 * individuals sorted alphabetically by surname key, then entities
 * (companies/trusts) sorted by display name. Purely a render-time
 * derivation — ordering is never persisted, and no secondary grouping
 * (e.g. by status) is applied.
 *
 * @returns {{ individuals: Array, entities: Array }}
 */
export function sortMonthClients(clients) {
  const individuals = [];
  const entities = [];
  for (const client of clients || []) {
    (client.isEntity ? entities : individuals).push(client);
  }
  individuals.sort((a, b) => compareNames(individualSurnameKey(a), individualSurnameKey(b)));
  entities.sort((a, b) => compareNames(a.displayName || '', b.displayName || ''));
  return { individuals, entities };
}

/**
 * Normalise a hand-typed name field: trim, collapse repeated internal
 * whitespace, and treat empty (or junk "NaN"/"-") input as null — the
 * SAME cell hygiene the atwork import applies (cleanCell above), so
 * edited names obey the rules imported ones do.
 */
export function normaliseNameInput(value) {
  return cleanCell(value);
}

/**
 * Derive display_name from the structured name columns — the import's
 * derivation (§34.3), re-exported for name editing so an edited client
 * regenerates the SAME natural form an imported one gets: entities use
 * the surname verbatim; individuals use `first surname` where first is
 * the first non-blank of nickname → initials (editing has no FullName
 * column to fall back on).
 */
export function deriveDisplayName({ surname, initials, nickname, isEntity }) {
  if (isEntity) return surname || '';
  const first = nickname || initials;
  return [first, surname].filter(Boolean).join(' ').trim();
}

/**
 * Render-time list name for a review client (CLAUDE.md §34.2):
 * individuals lead with the surname so the alphabetical month order is
 * visible at a glance — "Botes ME (Magda)". This NEVER changes what is
 * stored: display_name keeps the natural "Nickname Surname" form and
 * remains the source for any non-list surface.
 *
 *   - Entities: display_name verbatim.
 *   - Individuals with a surname column: surname + initials +
 *     "(nickname)", skipping blank parts (no empty brackets).
 *   - Individuals without one (manually added clients store only
 *     display_name): display_name verbatim, as typed.
 */
export function formatClientListName(client) {
  if (!client) return '';
  if (client.isEntity) return client.displayName || '';
  const surname = cleanCell(client.clientSurname);
  if (!surname) return client.displayName || '';
  const initials = cleanCell(client.clientInitials);
  const nickname = cleanCell(client.clientNickname);
  return [surname, initials, nickname ? `(${nickname})` : null]
    .filter(Boolean)
    .join(' ');
}

/**
 * ENTITY CHECKLIST RULE (CLAUDE.md §34.10) — structural, not just an
 * applicability default: entities have exactly two checklist items,
 * Annual Review and Commission Splits. S14 / LA Income NEVER apply to
 * an entity (no toggles rendered, no rows in any checklist, and the API
 * strips those fields for entity rows). Individuals keep all four with
 * the per-client applicability toggles.
 *
 * Every checklist rendering and the completion gate derive their item
 * set from this one helper.
 */
export function applicableChecklistItems(client) {
  return CHECKLIST_ITEMS.filter((item) => {
    if (!item.applicableField) return true; // AR / CS — always, everyone
    if (client.isEntity) return false; // S14 / LA — never for entities
    return client[item.applicableField] === true;
  });
}

/**
 * True when EVERY applicable checklist item is ticked (entities: AR +
 * CS; individuals: AR + CS + their applicable S14/LA). Built here, next
 * to applicableChecklistItems, so there is exactly one definition of
 * "fully ticked" — the derived-overdue check and the nudge counting
 * both use it (§34.4 / §34.9). Display/counting logic only: nothing
 * ever auto-changes a status because boxes are ticked.
 */
export function allApplicableItemsTicked(client) {
  return applicableChecklistItems(client).every((item) => client[item.doneField] === true);
}

/**
 * Completion gate (CLAUDE.md §34.9): the applicable checklist items that
 * are still unticked, in the fixed AR / 14 / LA / CS order. The
 * appointment_made → completed transition is blocked while this is
 * non-empty; the returned labels name the outstanding items in the
 * inline warning.
 */
export function getOutstandingChecklistItems(client) {
  return applicableChecklistItems(client)
    .filter((item) => !client[item.doneField])
    .map((item) => item.label);
}

/**
 * Daily nudge pacing (CLAUDE.md §34.9) — manual-with-nudge, no
 * automation: the Adviser pulls planned clients into the pipeline by
 * hand; this just suggests a sustainable daily rate.
 *
 *   remaining        = planned units anchored in the CURRENT SAST
 *                      month & year that are not yet fully ticked
 *                      (a group counts while ANY member is planned and
 *                      not fully ticked)
 *   workingDaysLeft  = business days from today through month-end
 *                      (counting today; weekends + SA public holidays
 *                      excluded via the shared isBusinessDay helper)
 *   suggestedToday   = clamp(ceil(remaining / max(workingDaysLeft, 1)),
 *                      0, remaining)
 *   nextClient       = first planned client of the current month by the
 *                      planner's existing sort order
 *   overdueCount     = planned units from EARLIER months with a member
 *                      still derived-overdue (checklist-aware
 *                      isReviewOverdue) — informational suffix
 *
 * January and December are closed months: the nudge collapses to the
 * quiet all-caught-up state (remaining 0, no suggestion).
 *
 * @param {Array} clients — active (non-completed) review clients
 * @param {{year: number, month: number}} now — getCurrentSASTYearMonth()
 */
export function computeReviewNudge(clients, now, groupLabels = {}) {
  const planned = (clients || []).filter((c) => c.status === 'planned');

  // MEETINGS, not clients (§34.10): a group is one unit (its members
  // share month + status by invariant), an ungrouped planned client is
  // one unit. Overdue counts units the same way.
  const plannedUnits = buildMonthUnits(planned, groupLabels);
  const unitAnchor = (u) => (u.type === 'group' ? u.members[0] : u.client);
  const unitMembers = (u) => (u.type === 'group' ? u.members : [u.client]);
  // A fully-ticked review no longer needs the Adviser's attention, so it
  // is neither "remaining" nor "overdue" here (§34.9 — checklist-aware
  // counting; a group counts while ANY member still has unticked
  // applicable items). Pulling a unit still pulls ALL members.
  const needsAttention = (u) => unitMembers(u).some((m) => !allApplicableItemsTicked(m));
  const overdueCount = plannedUnits.filter(
    (u) => unitMembers(u).some((m) => isReviewOverdue(m, now))
  ).length;

  if (!isOpenMonth(now.month)) {
    return { remaining: 0, workingDaysLeft: 0, suggestedToday: 0, nextUnit: null, overdueCount };
  }

  const currentMonthUnits = plannedUnits.filter((u) => {
    const anchor = unitAnchor(u);
    return anchor.reviewYear === now.year && anchor.reviewMonth === now.month && needsAttention(u);
  });
  const remaining = currentMonthUnits.length;
  const workingDaysLeft = countWorkingDaysLeftInMonth(now);
  const suggestedToday = remaining === 0
    ? 0
    : Math.min(remaining, Math.max(0, Math.ceil(remaining / Math.max(workingDaysLeft, 1))));

  // Next = first unit by the flat alphabetical order (buildMonthUnits
  // already sorted). Normalised for the nudge UIs.
  const first = currentMonthUnits[0] || null;
  const nextUnit = first
    ? {
        isGroup: first.type === 'group',
        label: first.type === 'group' ? first.label : first.client.displayName,
        memberCount: first.type === 'group' ? first.members.length : 1,
        memberIds: first.type === 'group' ? first.members.map((m) => m.id) : [first.client.id],
        displayName: first.type === 'group' ? first.label : first.client.displayName,
      }
    : null;

  return { remaining, workingDaysLeft, suggestedToday, nextUnit, overdueCount };
}

/**
 * Business days from today (SAST) through the end of the current month,
 * counting today. Reuses the SLA logic's holiday data via the shared
 * isBusinessDay helper (src/utils/businessDays.js) — the holiday list is
 * imported, slaMonitor itself is not.
 */
function countWorkingDaysLeftInMonth(now) {
  const today = getTodaySASTDayOfMonth();
  const daysInMonth = new Date(Date.UTC(now.year, now.month, 0)).getUTCDate();
  let count = 0;
  for (let d = today; d <= daysInMonth; d++) {
    // Noon UTC = 14:00 SAST — safely inside the same SAST calendar day.
    if (isBusinessDay(new Date(Date.UTC(now.year, now.month - 1, d, 12)))) count++;
  }
  return count;
}

/** Today's day-of-month in Africa/Johannesburg (from the shared SAST date string). */
function getTodaySASTDayOfMonth() {
  return parseInt(getTodaySASTDateString().slice(8, 10), 10);
}

/**
 * Checklist model (CLAUDE.md §34.5), in the fixed display order
 * AR / 14 / LA / CS. Annual Review and Commission Splits are always
 * applicable; Section 14 and LA Income are per-client toggles.
 */
export const CHECKLIST_ITEMS = [
  {
    key: 'AR',
    label: 'Annual Review',
    doneField: 'annualReviewDone',
    applicableField: null, // always applicable
  },
  {
    key: '14',
    label: 'Section 14 Fee Renewal',
    doneField: 's14Done',
    applicableField: 's14Applicable',
  },
  {
    key: 'LA',
    label: 'Living Annuity Income Review',
    doneField: 'laIncomeDone',
    applicableField: 'laIncomeApplicable',
  },
  {
    key: 'CS',
    label: 'Commission Splits',
    doneField: 'commissionSplitsDone',
    applicableField: null, // always applicable
  },
];
